import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import Cookies from "js-cookie";
import api from "../services";
import { getPlatformLogo } from "../services/apis";

function getCurrencySymbol(currencyCode: string): string {
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: currencyCode,
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value ?? currencyCode;
  } catch {
    return currencyCode;
  }
}
// Define your user type (customize as per your app)
export interface User {
  id: string;
  name: string;
  email: string;
  phonenumber: string;
  countryCode: string;
  discount: number;
  promotionalEmails?: boolean;
  promotionalTexts?: boolean;
  bonusWallet: {
    balance: number;
    pendingBalance: number;
    currency: string;
  };
  wallet: {
    balance: number;
    currency: string;
  };
  blackListForCash?: boolean;
  blackListForCard?: boolean;

  // add more fields if needed
}

// Define the context type
interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isAuth: boolean;
  login: (userData: User) => void;
  logout: () => void;
  /** Re-fetch wallet + bonus wallet balances (they change server-side, e.g. admin refunds) */
  refreshBalances: () => Promise<void>;
  squareApplicationId: string | null;
  squareLocationId: string | null;
  setSquareApplicationId: React.Dispatch<React.SetStateAction<string | null>>;
  setSquareLocationId: React.Dispatch<React.SetStateAction<string | null>>;
  adminSquareApplicationId: string | null;
  adminSquareLocationId: string | null;
  setAdminSquareApplicationId: React.Dispatch<React.SetStateAction<string | null>>;
  setAdminSquareLocationId: React.Dispatch<React.SetStateAction<string | null>>;
  currency: string;
  currencySymbol: string;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [squareApplicationId, setSquareApplicationId] = useState<string | null>(
    null,
  );
  const [squareLocationId, setSquareLocationId] = useState<string | null>(null);
  const [adminSquareApplicationId, setAdminSquareApplicationId] = useState<string | null>(
    null,
  );
  const [adminSquareLocationId, setAdminSquareLocationId] = useState<string | null>(null);
  const [currency,setCurrency] = useState<string>("USD");

  // Derived flag
  const isAuth = !!user;

  // Save user to localStorage on change
  useEffect(() => {
    if (user) {
    } else {
      localStorage.removeItem("user");
    }
  }, [user]);


  // Auth actions
  const login = (userData: User) => setUser(userData);
  const logout = () => {
    setUser(null);
    Cookies.remove("Logintoken");
  };
  // The user is loaded once at app start; balances can change on the server
  // afterwards (refunds, loyalty credits), so pages showing them call this.
  const refreshBalances = useCallback(async () => {
    const token = Cookies.get("Logintoken");
    if (!token) return;
    try {
      const res = await api.get("/verification", {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      const fresh = res.data?.success ? res.data.user : null;
      if (!fresh) return;
      setUser((prev) =>
        prev
          ? { ...prev, wallet: fresh.wallet ?? prev.wallet, bonusWallet: fresh.bonusWallet ?? prev.bonusWallet }
          : prev,
      );
    } catch (error) {
      console.error("Failed to refresh balances:", error);
    }
  }, []);
  // Fetch Square credentials on mount
  useEffect(() => {
    const fetchSquareCredentials = async () => {
      try {
        const response = await getPlatformLogo();
        if (response.data.currency) {
          setCurrency(response.data.currency);
        }
               if (response.data.success && response.data.square) {
          setAdminSquareApplicationId(
            response.data.square.SQUARE_APPLICATION_ID || null,
          );
          setAdminSquareLocationId(response.data.square.SQUARE_LOCATION_ID || null);
        }
      } catch (error) {
        console.error("Failed to fetch Square credentials:", error);
      }
    };
    fetchSquareCredentials();
  }, []);
  const currencySymbol = getCurrencySymbol(currency);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuth,
        login,
        logout,
        refreshBalances,
        squareApplicationId,
        squareLocationId,
        setSquareApplicationId,
        setSquareLocationId,
        adminSquareApplicationId,
        adminSquareLocationId,
        setAdminSquareApplicationId,
        setAdminSquareLocationId,
        currency,
        currencySymbol,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
